import { useState, useEffect } from "react";
import { useForm, validators } from "@hooks";
import { useNavigate, useParams } from 'react-router-dom';
import { useSpinnerContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import secureImg from "@illustrations/secure-400x284.png";
import { Button, ShowPassword } from "@common";

interface FormValues {
    password: string;
    confirm: string;
}

const ResetPassword = () => {

    const navigate = useNavigate();
    const params = useParams();
    const { setSpinnerShow } = useSpinnerContext();
    const { sessionId } = params;
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const initialState: FormValues = { password: ``, confirm: `` };

    useEffect(() => {
        (async () => {
            if (!sessionId) return;

            setSpinnerShow(true);
            try {
                await sunboundPayProxy.resetPasswordCheck(sessionId);
                setIsValid(true);
            } catch (error) {
                setIsValid(false);
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, []);

    const validations = {
        password: (val: string) => validators.validatePassword(val),
        confirm: (val: string, values: FormValues) => compare(val, values.password) || { confirm: `Passwords do not match` }
    };

    const compare = (val: string, match: string) => val === match;

    const submit = async () => {
        if (!sessionId) return;

        setSpinnerShow(true);
        try {
            await sunboundPayProxy.resetPassword(values.password, sessionId);
            navigate(`/reset-password-success`);
        } catch (error) {
            // setFormState(`Unable to process your request, please contact ${Constants.Help.Email} for support`);
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const { values, errors, changeHandler, submitHandler } = useForm(initialState, {}, validations, submit);

    const isFormSubmittable = values.password && values.confirm;

    return (
        <div className="flex flex-col w-full">
            {
                isValid &&
                <>
                    <div className="border-b border-neutral-100 pb-6 mb-6 md:mb-8">
                        <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">Reset Your Password</h1>
                    </div>
                    <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px] mb-6 md:mb-8">
                        Please enter your new password below.
                    </p>
                    <form method="POST" className="w-full flex flex-col gap-8" onSubmit={submitHandler} >
                        <div>
                            <ShowPassword
                                maxLength={100}
                                name="password"
                                placeholder="Password*"
                                value={values.password}
                                onChange={changeHandler}
                                helperText="Use at least 9 characters and include a mix of upper and lowercase letters, numbers, and special characters."
                                error={errors.password}
                            />
                        </div>
                        <ShowPassword
                            maxLength={100}
                            name="confirm"
                            placeholder="Confirm Password*"
                            value={values.confirm}
                            onChange={changeHandler}
                            error={errors.confirm}
                        />
                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-full"
                            disabled={!isFormSubmittable}
                        >
                            Reset Password
                        </Button>
                    </form>
                </>
            }
            {
                !isValid &&
                <div className="flex flex-col items-center text-center">
                    <div className="flex justify-center items-center h-[200px] mb-4">
                        <img src={secureImg} alt="Illustration of a laptop with a lock displayed on its screen, representing a secure password" className="w-[200px]" />
                    </div>
                    <h1 className="text-base leading-6 font-medium text-neutral-950 tracking-[0.16px] mb-4">
                        Link No Longer Active
                    </h1>
                    <p className="text-sm leading-5 text-neutral-800 tracking-[0.14px] mb-6 md:mb-8">
                        If you still need to reset your password, request a new link from the login page.
                    </p>
                    <Button
                        variant="primary"
                        size="lg"
                        className="w-full"
                        onClick={() => navigate(`/login`)}
                    >
                        Back to Login
                    </Button>
                </div>
            }
        </div>
    );
};

export default ResetPassword;
